import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@mui/styles";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import { DIALOG_BUTTON_BORDER_RADIUS, DIALOG_BUTTON_MIN_HEIGHT } from "./constants";
import { hasMeaningfulHtmlText } from "./utils";

const useStyles = makeStyles({
  dialogPaper: {
    maxWidth: "480px",
    width: "44vw",
    "& .MuiDialogContent-root": { padding: "0 !important" },
  },
  dialogHeader: {
    backgroundColor: "#4A7396",
    color: "white",
    padding: "8px 24px",
    textAlign: "center",
    width: "100%",
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  headerSubtitle: {
    fontSize: "15px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
    opacity: 0.95,
  },
  dialogBody: {
    padding: "20px 24px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#374151",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  input: {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    fontSize: "12px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "left",
    height: "30px",
    boxSizing: "border-box",
    "&:focus": {
      outline: "none",
      borderColor: "#4A7396",
    },
  },
  dialogFooter: {
    padding: "16px 24px",
    background: "#f9fafb",
    borderTop: "1px solid #e5e7eb",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
  },
  footerLeft: {
    justifySelf: "start",
  },
  footerCenter: {
    justifySelf: "center",
  },
});

function stripHtmlToText(html) {
  if (!html) return "";
  if (typeof document === "undefined") {
    return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const container = document.createElement("div");
  container.innerHTML = String(html);
  return (container.textContent || "").replace(/\s+/g, " ").trim();
}

function parseSubtitleParts(html) {
  if (!html) return { subtitlePart1: "", subtitlePart2: "" };
  const trimTrailingDash = (value) =>
    String(value || "")
      .replace(/\s*[—-]\s*$/, "")
      .trim();

  if (typeof document === "undefined") {
    const str = String(html);
    const spanMatch = str.match(/<span[^>]*>([\s\S]*?)<\/span>/i);
    const subtitlePart2 = spanMatch ? stripHtmlToText(spanMatch[1]) : "";
    const withoutSpan = str.replace(/<span[^>]*>[\s\S]*?<\/span>/gi, " ");
    const subtitlePart1 = trimTrailingDash(stripHtmlToText(withoutSpan));
    return { subtitlePart1, subtitlePart2 };
  }

  const container = document.createElement("div");
  container.innerHTML = String(html);
  const span = container.querySelector("span");
  const subtitlePart2 = span ? (span.textContent || "").replace(/\s+/g, " ").trim() : "";
  if (span) span.remove();
  const subtitlePart1 = trimTrailingDash(
    (container.textContent || "").replace(/\s+/g, " ").trim()
  );
  return { subtitlePart1, subtitlePart2 };
}

export default function EditHeaderTextDialog({
  open,
  onClose,
  areaName = "",
  miniTableTitle = "",
  miniTableSubTitle = "",
  onSave,
  isSaving = false,
}) {
  const classes = useStyles();
  const [titleText, setTitleText] = useState("");
  const [subtitlePart1, setSubtitlePart1] = useState("");
  const [subtitlePart2, setSubtitlePart2] = useState("");

  const parsedValues = useMemo(() => {
    const parsedTitle = stripHtmlToText(miniTableTitle);
    const parsedSubTitle = parseSubtitleParts(miniTableSubTitle);
    return {
      titleText: parsedTitle,
      subtitlePart1: parsedSubTitle.subtitlePart1,
      subtitlePart2: parsedSubTitle.subtitlePart2,
    };
  }, [miniTableTitle, miniTableSubTitle]);
  const showSubtitleInputs = useMemo(
    () => hasMeaningfulHtmlText(miniTableSubTitle),
    [miniTableSubTitle]
  );

  useEffect(() => {
    if (!open) return;
    setTitleText(parsedValues.titleText);
    setSubtitlePart1(parsedValues.subtitlePart1);
    setSubtitlePart2(parsedValues.subtitlePart2);
  }, [open, parsedValues]);

  const handleUpdate = () => {
    onSave?.({
      titleText,
      subtitlePart1: showSubtitleInputs ? subtitlePart1 : "",
      subtitlePart2: showSubtitleInputs ? subtitlePart2 : "",
    });
  };

  return (
    <MailProDialog
      open={open}
      onClose={() => { if (!isSaving) onClose?.(); }}
      disableEscapeKeyDown={isSaving}
      classes={{ paper: classes.dialogPaper }}
    >
      <MailProDialogHeader>
        <div className={classes.dialogHeader}>
          <div className={classes.headerTitle}>{areaName || "EDIT TABLE"}</div>
          <div className={classes.headerSubtitle}>Edit Header Text</div>
        </div>
      </MailProDialogHeader>
      <MailProDialogBody>
        <div className={classes.dialogBody}>
          <div className={classes.fieldWrap}>
            <label className={classes.label}>Title</label>
            <input
              type="text"
              className={classes.input}
              value={titleText}
              onChange={(e) => setTitleText(e.target.value)}
              placeholder="Enter title text"
            />
          </div>
          {showSubtitleInputs && (
            <>
              <div className={classes.fieldWrap}>
                <label className={classes.label}>Subtitle Text</label>
                <input
                  type="text"
                  className={classes.input}
                  value={subtitlePart1}
                  onChange={(e) => setSubtitlePart1(e.target.value)}
                  placeholder="Enter normal subtitle text"
                />
              </div>
              <div className={classes.fieldWrap}>
                <label className={classes.label}>Highlighted Subtitle Text</label>
                <input
                  type="text"
                  className={classes.input}
                  value={subtitlePart2}
                  onChange={(e) => setSubtitlePart2(e.target.value)}
                  placeholder="Enter highlighted subtitle text"
                />
              </div>
            </>
          )}
        </div>
      </MailProDialogBody>
      <div className={classes.dialogFooter}>
        <div className={classes.footerLeft}>
          <MailProButton
            variant="filled"
            label="Cancel"
            disabled={isSaving}
            onClick={onClose}
            customStyle={{
              fontSize: "12px",
              padding: "6px 14px",
              textTransform: "uppercase",
              fontWeight: "bold",
              minWidth: "auto",
              borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
              minHeight: DIALOG_BUTTON_MIN_HEIGHT,
              height: DIALOG_BUTTON_MIN_HEIGHT,
              boxSizing: "border-box",
            }}
          />
        </div>
        <div className={classes.footerCenter}>
          <MailProButton
            variant="filled"
            label={isSaving ? "Updating..." : "Update"}
            disabled={isSaving}
            onClick={handleUpdate}
            customStyle={{
              fontSize: "12px",
              padding: "8px 18px",
              textTransform: "uppercase",
              fontWeight: "bold",
              minWidth: "auto",
              borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
              minHeight: DIALOG_BUTTON_MIN_HEIGHT,
              height: DIALOG_BUTTON_MIN_HEIGHT,
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>
    </MailProDialog>
  );
}
